// Utility to hide scrollbar
// .scrollbar-hide::-webkit-scrollbar { display: none; }
import React, { useMemo, useState, useRef } from "react";
import { FiCheckCircle, FiLock, FiInfo, FiAlertCircle, FiClock, FiUserX, FiBox } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { Seat, Booking } from "./types";
import { useColorMode, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button, useDisclosure, Tooltip } from "@chakra-ui/react";

interface SeatGridProps {
  seats: Seat[];              // ALL seats on the floor
  allBookings: Booking[];     // ALL bookings for this date (active & pending)
  myUserId?: string;
  selectedDate: Date;
  loading: boolean;
  bookingInProgress: boolean;
  onSeatBook: (seat: Seat) => void;
  theme: any;
  floor?: { gridRows?: number; gridCols?: number; name?: string };
  canBook?: boolean;
  allowedSeatIds?: string[];
}

export const SeatGrid: React.FC<SeatGridProps> = ({
  seats,
  allBookings,
  myUserId,
  selectedDate,
  loading,
  bookingInProgress,
  onSeatBook,
  theme,
  floor,
  canBook = true,
  allowedSeatIds
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  
  // Confirmation Modal State
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedSeatToBook, setSelectedSeatToBook] = useState<Seat | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirmBooking = (seat: Seat) => {
    setSelectedSeatToBook(seat);
    onOpen();
  };

  const handleConfirm = () => {
    if (selectedSeatToBook) {
      onSeatBook(selectedSeatToBook);
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="w-10 h-10 border-4 border-[#B59143] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (seats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] opacity-40">
        <IconWrapper icon={FiInfo} size={48} className="mb-4 text-gray-400" />
        <p className="text-sm font-bold uppercase tracking-widest text-gray-500">No Booking Available</p>
        <p className="text-xs text-gray-400 mt-2">No active floor assignments for this date.</p>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .scrollbar-hide::-webkit-scrollbar {
              display: none;
          }
          .scrollbar-hide {
              -ms-overflow-style: none;
              scrollbar-width: none;
          }
        `}
      </style>
      <div className="w-full h-full flex flex-col overflow-hidden bg-transparent">
        {/* Scrollable Grid Container */}
        <div 
          className="flex-1 overflow-y-auto p-6 md:p-16 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
            <div className="mx-auto max-w-7xl">
              {/* Card Container for Grid */}
              <div className={`
                 p-6 rounded-2xl
                 ${isDark ? 'bg-transparent' : 'bg-transparent'}
              `}>
                  <div className="flex flex-wrap gap-4 justify-center">
                    {seats.sort((a, b) => (a.row - b.row) || (a.col - b.col)).map((seat) => {
                      // Determine Status
                      const booking = allBookings.find(b => b.seatId === seat.id);
                      const isMine = booking?.userId === myUserId;
                      const isPending = booking?.status === 'pending';
                      const isActive = booking?.status === 'active';
                      
                      const isBookedByOther = (isActive || isPending) && !isMine;
                      
                      let containerClasses = "";
                      let iconColor = "";
                      let textColor = "";
                      let tooltipLabel = "";
                      let icon = <IconWrapper icon={FiBox} size={24} />; // Default box icon

                      // Design based on User Request Image:
                      // Green: Available
                      // Blue: Mine (or "Active" in image context, but typically Blue is user's selection)
                      // Orange: Occupied/Locked

                      if (isMine) {
                          // MY SEAT (Blue per typical UX, or Green if "Active/Good")
                          // Let's stick to: Blue for Mine (Distinct)
                          containerClasses = isDark
                            ? "bg-blue-900/20 border-blue-500/50 hover:bg-blue-900/30"
                            : "bg-blue-50 border-blue-400 hover:bg-blue-100";
                          iconColor = "text-blue-500";
                          textColor = "text-blue-600 dark:text-blue-400";
                          tooltipLabel = isPending ? "Your Pending Request" : "Your Reserved Seat";
                          icon = <IconWrapper icon={FiCheckCircle} size={20} />;
                      } else if (isBookedByOther) {
                          // OCCUPIED (Orange)
                          containerClasses = isDark 
                            ? "bg-orange-900/10 border-orange-500/30 opacity-80 cursor-not-allowed" 
                            : "bg-orange-50 border-orange-400 opacity-80 cursor-not-allowed";
                          iconColor = "text-orange-400";
                          textColor = "text-orange-500 dark:text-orange-400";
                          tooltipLabel = "Occupied";
                          icon = <IconWrapper icon={FiBox} size={20} />;
                      } else if (!canBook) {
                           // VIEW ONLY
                           containerClasses = isDark 
                            ? "bg-gray-800/50 border-gray-700 cursor-not-allowed"
                            : "bg-gray-50 border-gray-400 cursor-not-allowed";
                           iconColor = "text-gray-500";
                           textColor = "text-gray-500";
                           tooltipLabel = "View Only";
                           icon = <IconWrapper icon={FiLock} size={20} />;
                      } else {
                          // AVAILABLE (Green)
                          containerClasses = isDark
                            ? "bg-green-900/10 border-green-500/30 hover:bg-green-900/20 hover:border-green-500 hover:shadow-[0_0_15px_rgba(34,197,94,0.15)]"
                            : "bg-green-50 border-green-400 hover:bg-green-100 hover:border-green-400 hover:shadow-md";
                          iconColor = "text-green-500";
                          textColor = "text-green-600 dark:text-green-400";
                          tooltipLabel = "Available";
                          icon = <IconWrapper icon={FiBox} size={20} />;
                      }

                      return (
                        <Tooltip 
                           key={seat.id} 
                           label={tooltipLabel}
                           hasArrow 
                           placement="top"
                           bg={isDark ? 'gray.700' : 'gray.800'}
                        >
                          <button
                            disabled={bookingInProgress || !!booking || !canBook}
                            onClick={() => confirmBooking(seat)}
                            className={`
                              relative w-16 h-16 rounded-xl border flex flex-col items-center justify-center gap-0.5 transition-all duration-200
                              ${containerClasses}
                            `}
                          >
                            <div className={`${iconColor} transition-colors`}>
                               {icon}
                            </div>
                            <span className={`text-[10px] font-bold ${textColor}`}>
                              {seat.name}
                            </span>
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
              </div>
            </div>
        </div>

        {/* Legend */}
        <div className={`flex-shrink-0 flex flex-wrap items-center justify-center gap-8 py-4 border-t ${isDark ? 'border-gray-800 bg-[#1A1C1F]' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center ${isDark ? 'bg-green-900/10 border-green-500/30' : 'bg-green-50 border-green-200'}`}>
                <IconWrapper icon={FiBox} size={16} className="text-green-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center ${isDark ? 'bg-blue-900/20 border-blue-500/50' : 'bg-blue-50 border-blue-300'}`}>
                <IconWrapper icon={FiCheckCircle} size={16} className="text-blue-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Your Seat</span>
          </div>

          <div className="flex items-center gap-2">
             <div className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center ${isDark ? 'bg-orange-900/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'}`}>
                <IconWrapper icon={FiBox} size={16} className="text-orange-400" />
             </div>
             <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Occupied</span>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
        preserveScrollBarGap
      >
        <AlertDialogOverlay className="backdrop-blur-sm bg-black/40">
          <AlertDialogContent className={`rounded-2xl ${isDark ? '!bg-[#1A1C1F] !border !border-[#2E2F33] !text-white' : '!bg-white'}`}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" className="flex items-center gap-2">
              <IconWrapper icon={FiAlertCircle} size={20} className="text-[#B59143]" />
              Confirm Booking
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to book <span className="font-bold text-[#B59143]">{selectedSeatToBook?.name}</span>? 
              <br/><br/>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                This will submit a request for approval.
              </span>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} size="sm" variant="ghost" mr={3}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirm} 
                size="sm"
                className={`!bg-[#B59143] !text-white hover:!bg-[#a38035]`}
              >
                Confirm Booking
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
